import { Children, KeyboardEvent, memo, MouseEvent, ReactNode, useCallback } from "react";
import { Scrollbars } from "@tecorien/mimic-v2";
import classnames from "classnames";
import BaseModal, { Props } from "react-modal";
import { useMergedRef } from "../../../utils";
import { Link, ProgressButton } from "../../ui";

import { ReactComponent as CloseIcon } from "./icons/closeIcon.svg";

import styles from "./style.module.scss";

const contentRef = (ref: HTMLDivElement) => {
  // fix modal height
  if (ref) {
    ref.style.height = "0";
    ref.style.overflow = "hidden";
    ref.style.padding = "0";
    setTimeout(() => {
      ref.style.height = "";
      ref.style.overflow = "";
      ref.style.padding = "";
    }, 0);
  }
};

export type BaseModalProps = Props;

export type ModalProps = {
  title?: string,
  onSubmit?: () => boolean | Promise<boolean>,
  scrollable?: boolean,
  submitText?: string,
  cancelText?: string,
  submitButtonDisabled?: boolean,
  cancelButtonDisabled?: boolean,
  children?: ReactNode,
  renderDefaultButtons?: boolean,
  showCloseButton?: boolean,
  contentClassName?: string,
  titleClassName?: string,
  buttonsClassName?: string,
  isSubmitting?: boolean,
  buttons?: ReactNode | ReactNode[],
} & BaseModalProps

export const Modal = memo<ModalProps>((
  {
    className,
    title,
    onRequestClose,
    submitText = "Save",
    cancelText = "Cancel",
    onSubmit = () => true,
    scrollable = true,
    overlayClassName,
    cancelButtonDisabled,
    submitButtonDisabled,
    renderDefaultButtons = true,
    showCloseButton,
    contentClassName,
    isSubmitting,
    children,
    buttons,
    titleClassName,
    buttonsClassName,
    shouldCloseOnEsc = true,
    ...rest
  }) => {
  const onSubmitClick = useCallback(async (e: MouseEvent | KeyboardEvent) => {
    if (await onSubmit?.()) {
      onRequestClose?.(e);
    }
  }, [ onSubmit, onRequestClose ]);

  return (
    <BaseModal
      shouldCloseOnEsc={shouldCloseOnEsc}
      {...rest}
      overlayClassName={classnames(styles.overlay, overlayClassName as string)}
      className={classnames(className as string, styles.root)}
      onRequestClose={onRequestClose}
      contentRef={useMergedRef(contentRef, rest.contentRef as any)}
    >
      <div className={classnames(styles.header, titleClassName)}>
        {title}
      </div>
      {
        showCloseButton &&
        <button
          tabIndex={-1}
          className={styles.closeButton}
          type="button"
          onClick={onRequestClose}
        >
          <CloseIcon />
        </button>
      }
      {
        scrollable
          ? (
            <Scrollbars
              className={classnames(contentClassName, styles.scrollbar, "os-host-flexbox")}
            >
              {children}
            </Scrollbars>
          )
          : (
            <div className={contentClassName}>
              {children}
            </div>
          )
      }
      {
        renderDefaultButtons &&
        <div className={classnames(styles.buttons, buttonsClassName)}>
          <Link
            className={styles.cancel}
            linkType="l"
            component="button"
            type="button"
            disabled={cancelButtonDisabled}
            onClick={onRequestClose}
          >
            <CloseIcon />
            {cancelText}
          </Link>
          <ProgressButton
            buttonType="primary"
            buttonColorSchema="default"
            type="submit"
            className={styles.submit}
            disabled={submitButtonDisabled}
            onClick={onSubmitClick}
            processing={isSubmitting}
          >
            {submitText}
          </ProgressButton>
        </div>
      }
      {
        (Children.count(buttons) > 0) &&
        <div className={classnames(styles.buttons, buttonsClassName)}>
          {buttons}
        </div>
      }
    </BaseModal>
  );
});

export default Modal;
